import { Component, type ReactNode } from "react";
import { trackEvent } from "@/lib/audit";
import { captureException as sentryCapture } from "@/lib/pr33/sentry";
import {
  captureRecoverySnapshot,
  hasAttemptedRecovery,
  markRecoveryAttempted,
} from "@/lib/stability/sessionRecovery";

interface State { hasError: boolean; error?: Error; recovering: boolean; countdown: number }

export class GlobalErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, recovering: false, countdown: 3 };
  private timer: ReturnType<typeof setInterval> | null = null;

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, recovering: false, countdown: 3 };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    console.error("[GlobalErrorBoundary]", error, info);
    sentryCapture(error, { component_stack: info.componentStack ?? "" });
    void trackEvent("global_error_boundary_caught", {
      message: error.message,
      stack: (error.stack ?? "").slice(0, 500),
      component_stack: (info.componentStack ?? "").slice(0, 500),
    });

    // Auto-recovery: at most ONE attempt per session.
    if (!hasAttemptedRecovery()) {
      try { captureRecoverySnapshot(); } catch { /* noop */ }
      this.setState({ recovering: true, countdown: 3 });
      this.timer = setInterval(() => {
        this.setState((prev) => {
          const next = prev.countdown - 1;
          if (next <= 0) {
            if (this.timer) { clearInterval(this.timer); this.timer = null; }
            try { markRecoveryAttempted(); } catch { /* noop */ }
            if (typeof window !== "undefined") {
              try { window.location.reload(); } catch { /* noop */ }
            }
            return { ...prev, countdown: 0 };
          }
          return { ...prev, countdown: next };
        });
      }, 1000);
    }
  }

  componentWillUnmount() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  reset = () => {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    this.setState({ hasError: false, error: undefined, recovering: false, countdown: 3 });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.recovering) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-6">
          <div className="max-w-md rounded-2xl border border-border/60 bg-card p-8 text-center shadow-2xl">
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <h1 className="text-lg font-black tracking-tight">Estamos recuperando la experiencia…</h1>
            <p className="mt-2 text-sm text-muted-foreground">Intentando restaurar tu sesión.</p>
            <p className="mt-3 text-xs text-muted-foreground/80">
              {this.state.countdown > 0 ? `Reintentando en ${this.state.countdown}s` : "Reintentando…"}
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-2xl border border-border/60 bg-card p-6 text-center shadow-2xl">
          <h1 className="text-lg font-black tracking-tight">Algo salió mal</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Hemos registrado el error. Puedes reintentar o volver al inicio.
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <button
              onClick={this.reset}
              className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
            >
              Reintentar
            </button>
            <a
              href="/"
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-bold"
            >
              Inicio
            </a>
          </div>
        </div>
      </div>
    );
  }
}